import React, { useState, useEffect } from 'react';
import { useCart } from '../Context/CartContext';
import { useUser } from '../Context/UserContext';
import { API_BASE_URL } from '../config';
import { Link, useNavigate } from 'react-router-dom';
import Toast from '../Components/Toast';
import '../Styles/Cart.css';



/* ── inline svg helpers ─────────────────────────────── */
const IconCart = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
);

const IconArrow = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
);

const IconChevronLeft = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

const IconTrash = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
    </svg>
);

const IconCheck = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const calculateDiscountedPrice = (price, type, value) => {
    const p = parseFloat(price);
    const v = parseFloat(value);
    if (!v || v <= 0) return p;
    if (type === 'percentage') {
        return p - (p * (v / 100));
    } else {
        return p - v;
    }
};

const getEffectiveDiscount = (product, variation) => {
    if (variation && variation.discount_is_active === 1 && parseFloat(variation.discount_value) > 0) {
        return { type: variation.discount_type, value: variation.discount_value };
    }
    if (product && product.discount_is_active === 1 && parseFloat(product.discount_value) > 0) {
        return { type: product.discount_type, value: product.discount_value };
    }
    return null;
};

const parseOffer = (product) => {
    if (!product) return null;

    let offerQty = product.offer_qty !== undefined && product.offer_qty !== null
        ? parseInt(product.offer_qty, 10)
        : null;
    let offerPrice = product.offer_price !== undefined && product.offer_price !== null
        ? parseFloat(product.offer_price)
        : null;

    if ((!offerQty || offerQty <= 0 || Number.isNaN(offerQty)) && product.offerDetails) {
        const [qty, price] = product.offerDetails.toString().split(':');
        offerQty = parseInt(qty, 10);
        offerPrice = parseFloat(price);
    }

    if (!offerQty || offerQty <= 0 || Number.isNaN(offerQty) || Number.isNaN(offerPrice)) return null;
    return { offerQty, offerPrice };
};

const getOfferTotal = (product, quantity) => {
    const offer = parseOffer(product);
    if (!offer) return null;

    const unitPrice = parseFloat(product.Price);
    if (Number.isNaN(unitPrice)) return null;

    const bundles = Math.floor(quantity / offer.offerQty);
    const remainder = quantity % offer.offerQty;
    const bundleTotal = bundles * offer.offerPrice;
    const remainderTotal = remainder * unitPrice;
    const total = bundleTotal + remainderTotal;

    return {
        bundles,
        remainder,
        offerQty: offer.offerQty,
        offerPrice: offer.offerPrice,
        total,
        averageUnitPrice: quantity > 0 ? total / quantity : unitPrice,
    };
};

/* ── component ──────────────────────────────────────── */
const Cart = () => {
    const { cart, removeFromCart, updateQuantity, cartTotal, clearCart, getProductGroupOfferInfo, getMixAndMatchOfferInfo, parseOffer } = useCart();
    const { user } = useUser();
    const navigate = useNavigate();
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [shippingSettings, setShippingSettings] = useState(null);
    const [orderDiscount, setOrderDiscount] = useState(null);

    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        const fetchDeliverySettings = async () => {
            try {
                let data;

                if (user && user.id) {
                    // Logged-in user: fetch city-aware delivery settings from backend
                    const response = await fetch(`${API_BASE_URL}/user/delivery-settings/${user.id}`);
                    if (response.ok) {
                        data = await response.json();
                    }
                } else {
                    // Guest: fetch Default delivery settings
                    const response = await fetch(`${API_BASE_URL}/admin/delivery-settings`);
                    if (response.ok) {
                        const allSettings = await response.json();
                        data = allSettings.find(item => (item.city || '').trim().toLowerCase() === 'default')
                            || allSettings[0];
                    }
                }

                if (data) {
                    setShippingSettings({
                        threshold: parseFloat(data.free_delivery_min || 0),
                        fee: parseFloat(data.delivery_fee || 0)
                    });
                }
            } catch (error) {
                console.error('Error fetching delivery settings:', error);
            }
        };

        const fetchOrderDiscount = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/admin/order-discounts`);
                if (response.ok) {
                    const data = await response.json();
                    // Find the first active discount
                    const activeDiscount = data.find(d => d.is_active === 1);
                    if (activeDiscount) {
                        setOrderDiscount(activeDiscount);
                    }
                }
            } catch (error) {
                console.error('Error fetching order discount:', error);
            }
        };


        fetchDeliverySettings();
        fetchOrderDiscount();
    }, [user]);

    const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0);

    const minOrderDiscount = parseFloat(orderDiscount?.min_price) || 0;
    const orderDiscountLabel = orderDiscount
        ? (orderDiscount.type?.toLowerCase() === 'percentage'
            ? `${orderDiscount.value}% off`
            : `$${parseFloat(orderDiscount.value).toFixed(2)} off`)
        : '';
    const amountToOrderDiscount = (orderDiscount && minOrderDiscount > 0)
        ? Math.max(minOrderDiscount - cartTotal, 0)
        : 0;

    // Only calculate if settings are loaded
    // threshold > 0: free shipping above that amount; threshold = 0: always charge the fee
    const deliveryFee = shippingSettings
        ? (shippingSettings.threshold > 0 && cartTotal >= shippingSettings.threshold ? 0 : shippingSettings.fee)
        : 0;

    // Calculate Discount
    let discountAmount = 0;
    const hasItemBenefits = cart.some(item => {
        const hasDiscount = getEffectiveDiscount(item.product, item.variation) !== null;
        const offerObj = parseOffer(item.product);
        const groupOffer = offerObj && offerObj.groupId
            ? getMixAndMatchOfferInfo(cart, offerObj.groupId)
            : getProductGroupOfferInfo(cart, item.product.id);
        const offerApplicable = groupOffer !== null && groupOffer.isApplicable;
        return hasDiscount || offerApplicable;
    });
    if (orderDiscount && !hasItemBenefits) {
        const minPrice = parseFloat(orderDiscount.min_price) || 0;
        if (cartTotal >= minPrice) {
            const dType = orderDiscount.type?.toLowerCase();
            const dValue = parseFloat(orderDiscount.value) || 0;

            if (dType === 'percentage') {
                discountAmount = (cartTotal * dValue) / 100;
            } else {
                discountAmount = dValue;
            }
        }
    }

    // Calculate Coupon Discount
    let couponDiscountAmount = 0;
    if (appliedCoupon) {
        const cType = appliedCoupon.discount_type?.toLowerCase();
        const cValue = parseFloat(appliedCoupon.discount_value) || 0;

        if (cType === 'percentage') {
            couponDiscountAmount = (cartTotal * cValue) / 100;
        } else {
            couponDiscountAmount = cValue;
        }
    }

    const totalDiscountAmount = Math.min(discountAmount + couponDiscountAmount, cartTotal);

    const finalTotal = cartTotal + deliveryFee - totalDiscountAmount;
    const progressPct = (shippingSettings && shippingSettings.threshold > 0)
        ? Math.min((cartTotal / shippingSettings.threshold) * 100, 100)
        : 0;
    const amountToFreeShipping = (shippingSettings && shippingSettings.threshold > 0)
        ? Math.max(shippingSettings.threshold - cartTotal, 0)
        : 0;

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;
        setIsApplyingCoupon(true);
        setCouponError('');
        try {
            const response = await fetch(`${API_BASE_URL}/user/apply-coupon`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: couponCode, user_id: user ? user.id : null })
            });
            const data = await response.json();
            if (response.ok) {
                setAppliedCoupon(data.coupon);
                setCouponCode('');
            } else {
                setCouponError(data.message || 'Invalid coupon code');
            }
        } catch (error) {
            setCouponError('Error applying coupon');
        } finally {
            setIsApplyingCoupon(false);
        }
    };

    const removeCoupon = () => {
        setAppliedCoupon(null);
        setCouponError('');
    };

    const handleCheckout = () => {
        if (!user) {
            navigate('/auth');
            return;
        }
        if (cart.length === 0) return;
        setShowConfirm(true);
    };

    const confirmCheckout = async () => {
        setShowConfirm(false);
        setIsCheckingOut(true);

        try {
            const orderData = {
                customer_id: user.id,
                items: cart.map(item => {
                    const offerObj = parseOffer(item.product);
                    const groupOffer = offerObj && offerObj.groupId
                        ? getMixAndMatchOfferInfo(cart, offerObj.groupId)
                        : getProductGroupOfferInfo(cart, item.product.id);
                    const discount = getEffectiveDiscount(item.product, item.variation);
                    const offer = getOfferTotal(item.product, item.quantity);
                    const unitPrice = parseFloat(item.product.Price);

                    let itemPrice = unitPrice;
                    let itemTotal = unitPrice * item.quantity;
                    let itemDiscount = 0;
                    let discountType = discount ? discount.type : null;
                    let discountValue = discount ? discount.value : 0;
                    let offerQty = null;
                    let offerPrice = null;

                    // Group offer takes priority
                    if (groupOffer && groupOffer.isApplicable) {
                        itemPrice = groupOffer.savingsPerUnit > 0
                            ? unitPrice - groupOffer.savingsPerUnit
                            : unitPrice;
                        itemTotal = itemPrice * item.quantity;
                        itemDiscount = (unitPrice * item.quantity) - itemTotal;
                        discountType = null;
                        discountValue = 0;
                        offerQty = groupOffer.offerQty;
                        offerPrice = groupOffer.offerPrice;
                    } else if (offer) {
                        itemPrice = offer.averageUnitPrice;
                        itemTotal = offer.total;
                        itemDiscount = (unitPrice * item.quantity) - offer.total;
                        discountType = null;
                        discountValue = 0;
                        offerQty = offer.offerQty;
                        offerPrice = offer.offerPrice;
                    } else if (discount) {
                        itemPrice = calculateDiscountedPrice(unitPrice, discount.type, discount.value);
                        itemTotal = itemPrice * item.quantity;
                        itemDiscount = unitPrice - itemPrice;
                    }

                    return {
                        product_id: item.product.id,
                        variation_id: item.variation ? item.variation.varID : null,
                        quantity: item.quantity,
                        price: unitPrice,
                        discount: itemDiscount,
                        discount_type: discountType,
                        discount_value: discountValue,
                        offer_qty: offerQty,
                        offer_price: offerPrice,
                        total: itemTotal
                    };
                }),
                total_amount: cartTotal,
                discount_amount: discountAmount,
                discount_type: (orderDiscount && discountAmount > 0) ? orderDiscount.type : null,
                discount_value: (orderDiscount && discountAmount > 0) ? orderDiscount.value : 0,
                coupon_code: appliedCoupon ? appliedCoupon.code : null,
                coupon_discount_value: appliedCoupon ? appliedCoupon.discount_value : 0,
                coupon_discount_type: appliedCoupon ? appliedCoupon.discount_type : null,
                delivery_fee: deliveryFee,
                final_amount: finalTotal,
                notes: appliedCoupon ? `Coupon used: ${appliedCoupon.code}` : ''
            };

            const response = await fetch(`${API_BASE_URL}/orders/place`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(orderData)
            });

            if (!response.ok) {
                throw new Error('Failed to place order');
            }

            clearCart();
            navigate('/orders');
        } catch (error) {
            console.error('Checkout error:', error);
            alert('Checkout failed. Please try again.');
        } finally {
            setIsCheckingOut(false);
        }
    };

    /* ── empty state ── */
    if (cart.length === 0) {
        return (
            <div className="cart-container">
                <div className="empty-cart">
                    <div className="empty-cart-icon"><IconCart /></div>
                    <h2>Your Cart is Empty</h2>
                    <p>Looks like you haven't added anything yet. Explore our collection to get started.</p>
                    <Link to="/shop" className="empty-shop-btn">
                        Browse Products <IconArrow />
                    </Link>
                </div>
            </div>
        );
    }

    /* ── main cart ── */
    return (
        <div className="cart-container">

            <div className="cart-content">

                {/* ── item list ── */}
                <div className="cart-items">
                    {cart.map((item, index) => {
                        const imgSrc =
                            item.variation?.photo
                                ? `${API_BASE_URL}${item.variation.photo}`
                                : item.product.varPhoto
                                    ? `${API_BASE_URL}${item.product.varPhoto}`
                                    : `${API_BASE_URL}${item.product.photo}`;

                        const variantLabel = item.variation
                            ? [item.variation.ColorName || item.variation.Color, item.variation.SizeName || item.variation.Size].filter(Boolean).join(' / ')
                            : null;


                        return (
                            <div
                                key={`${item.product.id}-${item.variation?.varID || index}`}
                                className="cart-item"
                                style={{ animationDelay: `${index * 0.06}s` }}
                            >
                                {/* image */}
                                <div className="cart-item-img-wrap">
                                    <img
                                        src={imgSrc}
                                        alt={item.product.name}
                                        className="cart-item-img"
                                        onError={(e) => { e.target.src = 'https://via.placeholder.com/110'; }}
                                    />
                                </div>

                                {/* info */}
                                <div className="cart-item-info">
                                    <h3>{item.product.name}</h3>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        {variantLabel && (
                                            <span className="cart-item-variant">{variantLabel}</span>
                                        )}
                                        {item.product.typeName && (
                                            <span className="cart-item-variant" style={{ background: 'rgba(235, 94, 40, 0.06)', border: '1px solid rgba(235, 94, 40, 0.15)', color: '#eb5e28' }}>
                                                Fabric: <strong style={{ fontWeight: '600', color: '#eb5e28' }}>{item.product.typeName}</strong>
                                            </span>
                                        )}
                                    </div>
                                    {(() => {
                                        const offerObj = parseOffer(item.product);
                                        const groupOffer = offerObj && offerObj.groupId
                                            ? getMixAndMatchOfferInfo(cart, offerObj.groupId)
                                            : getProductGroupOfferInfo(cart, item.product.id);
                                        const discount = getEffectiveDiscount(item.product, item.variation);
                                        const price = parseFloat(item.product.Price);

                                        // If there's a group offer and it's applicable
                                        if (groupOffer && groupOffer.isApplicable) {
                                            const discountedPrice = groupOffer.savingsPerUnit > 0
                                                ? price - groupOffer.savingsPerUnit
                                                : price;
                                            const isMixMatch = offerObj && offerObj.groupId;
                                            const offerLabel = isMixMatch
                                                ? `Mix & Match applied: ${groupOffer.offerQty} for $${groupOffer.offerPrice.toFixed(2)} (${groupOffer.totalQuantity} items)`
                                                : `Offer applied: ${groupOffer.offerQty} for $${groupOffer.offerPrice.toFixed(2)} (${groupOffer.totalQuantity} total units)`;

                                            return (
                                                <div className="cart-item-price-row">
                                                    <span className="cart-item-price discounted">
                                                        ${discountedPrice.toFixed(2)}
                                                    </span>
                                                    <span className="cart-item-price-old">${price.toFixed(2)}</span>
                                                    <span className="cart-item-discount-tag">
                                                        {offerLabel}
                                                    </span>
                                                </div>
                                            );
                                        }

                                        // Fall back to individual item offer
                                        const offer = getOfferTotal(item.product, item.quantity);
                                        if (offer) {
                                            const displayPrice = offer.averageUnitPrice;
                                            const offerLabel = item.quantity >= offer.offerQty
                                                ? `Offer applied: ${offer.offerQty} for $${offer.offerPrice.toFixed(2)}`
                                                : `Buy ${offer.offerQty} for $${offer.offerPrice.toFixed(2)}`;

                                            return (
                                                <div className="cart-item-price-row">
                                                    <span className="cart-item-price discounted">
                                                        ${displayPrice.toFixed(2)}
                                                    </span>
                                                    <span className="cart-item-price-old">${price.toFixed(2)}</span>
                                                    <span className="cart-item-discount-tag">
                                                        {offerLabel}
                                                    </span>
                                                </div>
                                            );
                                        }

                                        if (discount) {
                                            return (
                                                <div className="cart-item-price-row">
                                                    <span className="cart-item-price discounted">
                                                        ${calculateDiscountedPrice(price, discount.type, discount.value).toFixed(2)}
                                                    </span>
                                                    <span className="cart-item-price-old">${price.toFixed(2)}</span>
                                                    <span className="cart-item-discount-tag">
                                                        {discount.type === 'percentage' ? `-${Math.round(discount.value)}%` : `-$${discount.value}`}
                                                    </span>
                                                </div>
                                            );
                                        }
                                        return <div className="cart-item-price">${price.toFixed(2)}</div>;
                                    })()}
                                </div>

                                {/* controls */}
                                <div className="cart-item-actions">
                                    <div className="quantity-controls">
                                        <button
                                            className="qty-btn"
                                            onClick={() => updateQuantity(item.product.id, item.variation?.varID, item.quantity - 1)}
                                            aria-label="Decrease quantity"
                                        >−</button>
                                        <span className="qty-display">{item.quantity}</span>
                                        <button
                                            className="qty-btn"
                                            onClick={() => {
                                                const stock = item.variation ? item.variation.Qty : item.product.StockQTY;
                                                const stockNum = parseInt(stock || 0);
                                                const maxQty = stockNum;

                                                if (item.quantity >= maxQty) {
                                                    setNotification({ message: 'Cannot add more than available stock', type: 'error' });
                                                } else {
                                                    updateQuantity(item.product.id, item.variation?.varID, item.quantity + 1);
                                                }
                                            }}
                                            aria-label="Increase quantity"
                                        >+</button>
                                    </div>

                                    <button
                                        className="remove-btn"
                                        onClick={() => removeFromCart(item.product.id, item.variation?.varID)}
                                        aria-label="Remove item"
                                    >
                                        <IconTrash /> Remove
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* ── order summary ── */}
                <aside className="cart-summary">
                    <p className="cart-summary-title">Order Summary</p>

                    <div className="summary-row">
                        <span>Subtotal ({totalItems} {totalItems === 1 ? 'item' : 'items'})</span>
                        <span>${cartTotal.toFixed(2)}</span>
                    </div>

                    <div className="summary-row">
                        <span>Shipping</span>
                        <span className={deliveryFee === 0 ? 'summary-shipping-free' : ''}>
                            {shippingSettings ? (deliveryFee === 0 ? (shippingSettings.threshold > 0 ? 'Free' : '') : `$${shippingSettings.fee.toFixed(2)}`) : '...'}
                        </span>
                    </div>

                    {deliveryFee === 0 && shippingSettings && shippingSettings.threshold === 0 && (
                        <div className="summary-row summary-note" style={{ marginTop: '-8px', marginBottom: '8px' }}>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-sub)' }}>
                                The delivery fee for your city will be sent to you.
                            </span>
                        </div>
                    )}

                    {orderDiscount && amountToOrderDiscount > 0 && !hasItemBenefits && (
                        <div className="summary-row summary-note">
                            <span>Order Discount available</span>
                            <span>Spend ${amountToOrderDiscount.toFixed(2)} more to unlock {orderDiscountLabel} on orders above ${minOrderDiscount.toFixed(2)}</span>
                        </div>
                    )}

                    {orderDiscount && hasItemBenefits && amountToOrderDiscount === 0 && (
                        <div className="summary-row summary-note">
                            <span>Order Discount available</span>
                            <span>Product-level or bundle discounts are already applied, so the order discount will not stack.</span>
                        </div>
                    )}

                    {discountAmount > 0 && (
                        <div className="summary-row summary-discount">
                            <span>
                                Order Discount ({orderDiscountLabel}{minOrderDiscount > 0 ? ` on orders above $${minOrderDiscount.toFixed(2)}` : ''})
                            </span>
                            <span>-${discountAmount.toFixed(2)}</span>
                        </div>
                    )}

                    {appliedCoupon && (
                        <div className="summary-row summary-discount coupon-applied">
                            <span>
                                Coupon: <strong>{appliedCoupon.code}</strong>
                                <button className="remove-coupon-btn" onClick={removeCoupon}>×</button>
                            </span>
                            <span>-${couponDiscountAmount.toFixed(2)}</span>
                        </div>
                    )}

                    <div className="coupon-section">
                        {!appliedCoupon ? (
                            <div className="coupon-input-group">
                                <input
                                    type="text"
                                    placeholder="Enter Coupon Code"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value)}
                                    className={couponError ? 'input-error' : ''}
                                />
                                <button
                                    onClick={handleApplyCoupon}
                                    disabled={isApplyingCoupon || !couponCode.trim()}
                                >
                                    {isApplyingCoupon ? '...' : 'Apply'}
                                </button>
                            </div>
                        ) : null}
                        {couponError && <p className="coupon-error">{couponError}</p>}
                    </div>

                    {/* free-shipping progress */}
                    {deliveryFee > 0 ? (
                        <div className="shipping-progress-wrap">
                            <p className="shipping-progress-label">
                                Add <strong>${amountToFreeShipping.toFixed(2)}</strong> more for free shipping
                            </p>
                            <div className="shipping-progress-bar">
                                <div className="shipping-progress-fill" style={{ width: `${progressPct}%` }} />
                            </div>
                        </div>
                    ) : (
                        shippingSettings && shippingSettings.threshold > 0 && (
                            <div className="shipping-success-msg">
                                <IconCheck /> You've earned <strong>Free Shipping</strong>!
                            </div>
                        )
                    )}

                    <div className="summary-divider" />

                    <div className="summary-total">
                        <span className="summary-total-label">Total</span>
                        <span className="summary-total-amount">${finalTotal.toFixed(2)}</span>
                    </div>

                    <button
                        id="checkout-btn"
                        className="checkout-btn"
                        onClick={handleCheckout}
                        disabled={isCheckingOut || !shippingSettings}
                    >
                        {isCheckingOut
                            ? 'Processing…'
                            : (<>Proceed to Checkout <IconArrow /></>)
                        }
                    </button>

                    <Link to="/shop" className="continue-shopping">
                        <IconChevronLeft /> Continue Shopping
                    </Link>

                    <div className="cart-summary-notice">
                        <span>No refund</span>
                        <span>Exchange in 24 hours</span>
                    </div>
                </aside>

            </div>

            {/* Confirmation Modal */}
            {showConfirm && (
                <div className="confirm-overlay" onClick={() => setShowConfirm(false)}>
                    <div className="confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="confirm-icon">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                        </div>
                        <h2>Ready to Checkout?</h2>
                        <p>You're about to place an order for <strong>{totalItems} {totalItems === 1 ? 'item' : 'items'}</strong> with a total of <strong>${finalTotal.toFixed(2)}</strong>. Would you like to proceed?</p>
                        <div className="confirm-actions">
                            <button className="confirm-btn confirm-btn-cancel" onClick={() => setShowConfirm(false)}>
                                Review Cart
                            </button>
                            <button className="confirm-btn confirm-btn-proceed" onClick={confirmCheckout}>
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {notification && (
                <Toast
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}
        </div>
    );
};

export default Cart;
